import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  fmtDate,
  photoThumbUrl,
  photoUrl,
  type Album,
  type AlbumContents,
  type Photo,
} from '../api';
import { useData } from '../App';
import { DeleteButton, Empty, Icon, IconButton } from '../ui';
import { fileSize, prepare } from '../photos';

/**
 * The photo archive.
 *
 * Two screens: the albums, and one album's pictures. Pictures are resized in
 * the browser before they are sent (see ../photos.ts), which is what makes it
 * possible to pick twenty photos off a phone at a club night and not wait.
 */

type Progress = { done: number; total: number; failed: number } | null;

export default function Myndir() {
  const { events } = useData();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => setAlbums(await api.albums()), []);
  useEffect(() => void load(), [load]);

  async function act(fn: () => Promise<Album[]>) {
    setBusy(true);
    try {
      setAlbums(await fn());
    } finally {
      setBusy(false);
    }
  }

  if (openId !== null) {
    return (
      <AlbumView
        id={openId}
        onBack={() => {
          setOpenId(null);
          void load();
        }}
      />
    );
  }

  return (
    <div className="view">
      <section className="card col-8">
        <div className="card-h">
          <h2>Albúm</h2>
          <span className="lbl">
            {albums.length} albúm · {albums.reduce((n, a) => n + a.photo_count, 0)} myndir
          </span>
        </div>

        {albums.length === 0 ? (
          <Empty title="Engin albúm enn" body="Stofnaðu albúm hér til hliðar og settu inn myndir." />
        ) : (
          <div className="alb-grid">
            {albums.map((a) => (
              <button key={a.id} type="button" className="alb" onClick={() => setOpenId(a.id)}>
                <span className="alb-cover">
                  {a.cover !== null ? (
                    <img src={photoThumbUrl(a.cover)} alt="" loading="lazy" />
                  ) : (
                    <span className="alb-empty">
                      <Icon.picture />
                    </span>
                  )}
                  {a.photo_count > 0 && <span className="alb-n">{a.photo_count}</span>}
                </span>
                <span className="alb-name">{a.name}</span>
                <span className="alb-meta">{fmtDate(a.created_at.slice(0, 10))}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="card col-4">
        <div className="card-h"><h2>Nýtt albúm</h2></div>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const n = name.trim();
            if (!n) return;
            void act(() => api.addAlbum({ name: n })).then(() => setName(''));
          }}
        >
          <div className="fld">
            <label className="lbl" htmlFor="alb-name">Heiti</label>
            <input
              id="alb-name"
              type="text"
              value={name}
              placeholder="t.d. Sealed-kvöld í september"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button className="btn" disabled={busy || !name.trim()}>Stofna albúm</button>
          <p className="hint" style={{ lineHeight: 1.5 }}>
            Myndir eru minnkaðar í símanum áður en þær fara upp, svo það er fljótlegt að setja
            inn margar í einu. Albúm má tengja við viðburð þegar það er opnað.
          </p>
        </form>

        {events.length > 0 && albums.some((a) => a.event_id !== null) && (
          <p className="hint" style={{ marginTop: 14 }}>
            {albums.filter((a) => a.event_id !== null).length} albúm eru tengd viðburði.
          </p>
        )}
      </section>
    </div>
  );
}

/* ── one album ──────────────────────────────────────────────────────── */

function AlbumView({ id, onBack }: { id: number; onBack: () => void }) {
  const { events } = useData();
  const [data, setData] = useState<AlbumContents | null>(null);
  const [progress, setProgress] = useState<Progress>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [over, setOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => setData(await api.albumPhotos(id)), [id]);
  useEffect(() => void load(), [load]);

  /**
   * Upload picked files one at a time.
   *
   * Sequentially on purpose: twenty photos at once would mean twenty 24 MB
   * request bodies in flight from a phone, and the progress line is only
   * honest if the work is actually ordered.
   */
  async function upload(files: FileList | File[]) {
    const list = [...files].filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) return;
    setProgress({ done: 0, total: list.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < list.length; i++) {
      try {
        const ready = await prepare(list[i]!);
        if (!ready) throw new Error('unreadable');
        await api.addPhoto(id, ready);
      } catch {
        failed += 1;
      }
      setProgress({ done: i + 1, total: list.length, failed });
    }
    await load();
    // Leave the tally up briefly so a failure is actually seen.
    setTimeout(() => setProgress(null), failed ? 6000 : 1200);
  }

  if (!data) {
    return (
      <div className="view">
        <section className="card"><p className="lbl">Sæki albúmið…</p></section>
      </div>
    );
  }

  const { album, photos } = data;
  const shown = viewing === null ? null : photos.find((p) => p.id === viewing) ?? null;
  const totalBytes = photos.reduce((n, p) => n + p.bytes, 0);

  async function patch(d: Parameters<typeof api.updateAlbum>[1]) {
    await api.updateAlbum(id, d);
    await load();
  }

  return (
    <div className="view">
      <section className="card col-12 mot-head">
        <button type="button" className="btn ghost sm" onClick={onBack}>← Öll albúm</button>
        {renaming ? (
          <form
            className="row"
            style={{ gap: 8, flex: '1 1 auto' }}
            onSubmit={(e) => {
              e.preventDefault();
              const n = draftName.trim();
              if (n) void patch({ name: n }).then(() => setRenaming(false));
            }}
          >
            <input type="text" value={draftName} autoFocus onChange={(e) => setDraftName(e.target.value)} />
            <button className="btn sm">Vista</button>
            <button type="button" className="btn ghost sm" onClick={() => setRenaming(false)}>Hætta við</button>
          </form>
        ) : (
          <>
            <h2>{album.name}</h2>
            <IconButton
              label="Breyta heiti"
              onClick={() => {
                setDraftName(album.name);
                setRenaming(true);
              }}
            >
              <Icon.pencil />
            </IconButton>
          </>
        )}
        <span className="lbl">
          {photos.length} myndir{totalBytes ? ` · ${fileSize(totalBytes)}` : ''}
        </span>
      </section>

      <section className="card col-12">
        <div className="card-h">
          <h2>Myndir</h2>
          <div className="row" style={{ gap: 8 }}>
            <select
              value={album.event_id ?? ''}
              aria-label="Tengja við viðburð"
              onChange={(e) => void patch({ event_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">Ótengt viðburði</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.name} · {fmtDate(ev.event_date)}
                </option>
              ))}
            </select>
            <button className="btn sm" disabled={!!progress} onClick={() => fileRef.current?.click()}>
              Setja inn myndir
            </button>
            <DeleteButton
              label="Eyða albúmi"
              onClick={() => {
                if (!window.confirm(`Eyða albúminu „${album.name}“ og öllum ${photos.length} myndum?`)) return;
                void api.delAlbum(id).then(onBack);
              }}
            />
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) void upload(e.target.files);
            e.target.value = '';
          }}
        />

        {progress ? (
          <div className="up-bar">
            <span className="up-fill" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
            <span className="up-txt">
              {progress.done} af {progress.total}
              {progress.failed ? ` · ${progress.failed} mistókust` : ''}
            </span>
          </div>
        ) : null}

        <div
          className={`drop${over ? ' over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
          }}
        >
          {photos.length === 0 ? (
            <Empty title="Engar myndir enn" body="Dragðu myndir hingað eða ýttu á „Setja inn myndir“." />
          ) : (
            <div className="ph-grid">
              {photos.map((p) => (
                <button key={p.id} type="button" className="ph" onClick={() => setViewing(p.id)}>
                  <img src={photoThumbUrl(p.id)} alt={p.caption} loading="lazy" />
                  {album.cover_photo_id === p.id && <span className="ph-cover">Forsíða</span>}
                  {p.caption && <span className="ph-cap">{p.caption}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {shown && (
        <Lightbox
          photo={shown}
          photos={photos}
          isCover={album.cover_photo_id === shown.id}
          onGo={setViewing}
          onClose={() => setViewing(null)}
          onChanged={load}
          onCover={() => void patch({ cover_photo_id: shown.id })}
        />
      )}
    </div>
  );
}

/* ── one picture, large ─────────────────────────────────────────────── */

function Lightbox({
  photo,
  photos,
  isCover,
  onGo,
  onClose,
  onChanged,
  onCover,
}: {
  photo: Photo;
  photos: Photo[];
  isCover: boolean;
  onGo: (id: number) => void;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onCover: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [caption, setCaption] = useState(photo.caption);

  const at = photos.findIndex((p) => p.id === photo.id);
  const step = useCallback(
    (by: number) => {
      const next = photos[at + by];
      if (next) onGo(next.id);
    },
    [photos, at, onGo],
  );

  useEffect(() => {
    ref.current?.showModal();
  }, []);
  useEffect(() => setCaption(photo.caption), [photo.id, photo.caption]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [step]);

  async function saveCaption() {
    if (caption === photo.caption) return;
    await api.updatePhoto(photo.id, { caption });
    await onChanged();
  }

  return (
    <dialog
      className="lb"
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      aria-label={photo.caption || 'Mynd'}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="lb-in">
        <img src={photoUrl(photo.id)} alt={photo.caption} className="lb-img" />

        <div className="lb-bar">
          <button
            type="button"
            className="btn ghost sm"
            disabled={at <= 0}
            onClick={() => step(-1)}
          >
            ←
          </button>
          <span className="lbl">{at + 1} af {photos.length}</span>
          <button
            type="button"
            className="btn ghost sm"
            disabled={at >= photos.length - 1}
            onClick={() => step(1)}
          >
            →
          </button>

          <input
            type="text"
            className="lb-cap"
            value={caption}
            placeholder="Myndatexti…"
            onChange={(e) => setCaption(e.target.value)}
            onBlur={() => void saveCaption()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />

          <button type="button" className="btn ghost sm" onClick={onCover} disabled={isCover}>
            {isCover ? 'Forsíðumynd' : 'Gera að forsíðu'}
          </button>
          <a className="btn ghost sm" href={photoUrl(photo.id)} download={`mynd-${photo.id}.${photo.ext}`}>
            <Icon.download /> Sækja
          </a>
          <DeleteButton
            label="Eyða mynd"
            onClick={() => {
              if (!window.confirm('Eyða myndinni?')) return;
              void api.delPhoto(photo.id).then(async () => {
                // Step to the neighbour rather than dropping the viewer; the
                // usual reason to open a photo is to decide whether to keep it.
                const next = photos[at + 1] ?? photos[at - 1];
                await onChanged();
                if (next) onGo(next.id);
                else onClose();
              });
            }}
          />
          <button type="button" className="cw-x" onClick={onClose} aria-label="Loka">×</button>
        </div>

        <p className="lb-meta">
          {photo.width}×{photo.height} · {fileSize(photo.bytes)} · {fmtDate(photo.created_at.slice(0, 10))}
        </p>
      </div>
    </dialog>
  );
}
